import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { TMableProduct } from '@/core/constants/event_types/extended_mable_event';
import { Logger } from '@/shared/logger/logger';

import {
  calculateGA4EventMapping,
  GA4Event,
  GA4EventParameters,
  GA4Item,
} from './types';

/**
 * Helper function to create a GA4 item from a Mable product
 * @param product The Mable product
 * @returns GA4 formatted item
 */
function createGA4Item(product: TMableProduct): GA4Item {
  const item: Partial<GA4Item> = {};

  if (product.cur) {
    item.currency = product.cur;
  }

  const discount = product.totalDiscount || 0;
  if (discount > 0) {
    item.discount = discount;
  }

  if (product.br) {
    item.item_brand = product.br;
  }

  if (product.id) {
    item.item_id = product.id;
  }

  if (product.nam) {
    item.item_name = product.nam;
  }

  const price = Number.parseFloat(product.prc);
  if (price > 0) {
    item.price = price;
  }

  const quantity = product.qty || 1;
  if (quantity > 0) {
    item.quantity = quantity;
  }

  // Enhanced product details
  if (product.cat) {
    // Handle category hierarchy
    const categories = product.cat.split('/');
    if (categories[0]) item.item_category = categories[0];
    if (categories[1]) item.item_category2 = categories[1];
    if (categories[2]) item.item_category3 = categories[2];
    if (categories[3]) item.item_category4 = categories[3];
    if (categories[4]) item.item_category5 = categories[4];
  }

  if (product.var) {
    item.item_variant = product.var;
  }

  // Product identifiers
  if (product.ids) {
    if (product.ids.sku) item.item_id = product.ids.sku;
    if (product.ids.pid) item.product_id = product.ids.pid;
    if (product.ids.vid) item.variant_id = product.ids.vid;
    if (product.ids.pnu) item.product_number = product.ids.pnu;
  }

  // Discount information
  if (product.discounts?.length && product.discounts[0]?.code) {
    item.coupon = product.discounts[0].code;
  }

  return item as GA4Item;
}

/**
 * Transforms a Mable event to a GA4 event format
 * @param mableEvent The Mable event to transform
 * @returns GA4 formatted event
 */
export function transformMableEventToGA4Event(
  mableEvent: TMableEvent,
): GA4Event {
  const { bd, cdas, eid, en: eventName, esd, ii, pd, pids, sd } = mableEvent;

  let ga4EventName = calculateGA4EventMapping(eventName, esd?.cen);

  const eventParams: GA4EventParameters = {};

  if (eid) {
    eventParams.event_id = eid;
  }

  // Common parameters
  if (pd) {
    if (pd.dl) eventParams.page_location = pd.dl;
    if (pd.rl) eventParams.page_referrer = pd.rl;
    if (pd.dt) eventParams.page_title = pd.dt;
  }

  if (ii) {
    eventParams.user_id = ii;
  }

  if (bd) {
    if (bd.ua) eventParams.user_agent = bd.ua;
    if (bd.ul) eventParams.user_language = bd.ul;
    if (bd.srw && bd.srh) {
      eventParams.screen_resolution = `${bd.srw}x${bd.srh}`;
    }
  }

  if (sd) {
    if (sd.sid) eventParams.session_id = sd.sid;
    if (sd.sct) eventParams['session_count'] = sd.sct;
    if (sd.ip4 || sd.ip6) {
      eventParams.ip_address = sd.ip4 || sd.ip6;
    }

    if (sd.iqpar) {
      if (sd.iqpar['utm_source']?.[0])
        eventParams.utm_source = sd.iqpar['utm_source'][0];
      if (sd.iqpar['utm_medium']?.[0])
        eventParams.utm_medium = sd.iqpar['utm_medium'][0];
      if (sd.iqpar['utm_campaign']?.[0])
        eventParams.utm_campaign = sd.iqpar['utm_campaign'][0];
      if (sd.iqpar['utm_term']?.[0])
        eventParams.utm_term = sd.iqpar['utm_term'][0];
      if (sd.iqpar['utm_content']?.[0])
        eventParams.utm_content = sd.iqpar['utm_content'][0];
    }
  }

  if (esd?.cart) {
    if (esd.cart.total) {
      const shipping = Number(esd.cart.total.shipping || 0);
      const tax = Number(esd.cart.total.tax || 0);
      const discount = Number(esd.cart.total.discount || 0);

      if (shipping > 0) eventParams.shipping = shipping;
      if (tax > 0) eventParams.tax = tax;
      if (discount > 0) eventParams.discount = discount;
    }

    if (esd.pay_methods?.length) {
      eventParams.payment_type = esd.pay_methods[0];
    }

    if (esd.cart.quantity) {
      eventParams.num_items = esd.cart.quantity;
    }

    if (esd.cart.discounts?.length && esd.cart.discounts[0]?.code) {
      eventParams.coupon = esd.cart.discounts[0].code;
    }
  }

  if (esd?.cv_data) {
    const originalValue = Number(esd.cv_data.org_cv || 0);
    const discountAmount = Number(esd.cv_data.discount || 0);
    const shippingAmount = Number(esd.cv_data.shipping || 0);
    const taxAmount = Number(esd.cv_data.tax || 0);

    if (originalValue > 0) eventParams.original_value = originalValue;
    if (discountAmount > 0) eventParams.discount_amount = discountAmount;
    if (shippingAmount > 0) eventParams.shipping_amount = shippingAmount;
    if (taxAmount > 0) eventParams.tax_amount = taxAmount;
  }

  if (cdas) {
    if (cdas.street?.length) eventParams.user_address = cdas.street[0];
    if (cdas.zip?.length) eventParams.user_postal_code = cdas.zip[0];
    if (cdas.ct?.length) eventParams.user_city = cdas.ct[0];
    if (cdas.rg?.length) eventParams.user_region = cdas.rg[0];
    if (cdas.cc?.length) eventParams.user_country = cdas.cc[0];
  }

  if (pids) {
    if (pids.gclid?.length) eventParams.gclid = pids.gclid[0];
    if (pids.fbclid?.length) eventParams.fbclid = pids.fbclid[0];
    if (pids.ttclid?.length) eventParams.ttclid = pids.ttclid[0];
    if (pids.gacid?.length) eventParams.gacid = pids.gacid[0];
  }

  // Process event-specific data
  try {
    switch (eventName) {
      case EMableEventName.ORDER_COMPLETED: {
        const currency = esd?.cart?.currency || esd?.act_product?.cur;
        if (currency) {
          eventParams.currency = currency;
        }

        const value =
          esd?.cv_data?.cv === undefined
            ? Number(esd?.cart?.total?.price || 0)
            : Number(esd.cv_data.cv);
        if (value > 0) {
          eventParams.value = value;
        }

        if (esd?.oid) {
          eventParams.transaction_id = esd.oid;
        }

        if (esd?.pay_methods?.length) {
          eventParams.pay_methods = esd.pay_methods;
        }
        break;
      }

      case EMableEventName.PRODUCT_ADDED: {
        if (esd?.act_product) {
          const product = esd.act_product;
          if (product.cur) {
            eventParams.currency = product.cur;
          }

          const value = Number(product.prc) * (esd?.qty_act_product || 1);
          if (value > 0) {
            eventParams.value = value;
          }

          eventParams.items = [createGA4Item(product)];
        }
        break;
      }

      case EMableEventName.CHECKOUT_STARTED: {
        if (esd?.cart) {
          const cart = esd.cart;
          if (cart.currency) {
            eventParams.currency = cart.currency;
          }

          const value = Number(cart.total?.price || 0);
          if (value > 0) {
            eventParams.value = value;
          }

          if (cart?.discounts?.[0]?.code) {
            eventParams.coupon = cart.discounts[0].code;
          }

          if (cart.products?.length) {
            eventParams.items = cart.products.map(createGA4Item);
          }
        }
        break;
      }

      case EMableEventName.PAGE_VIEWED: {
        break;
      }

      case EMableEventName.POST_PURCHASE_SURVEY: {
        break;
      }

      case EMableEventName.PAYMENT_INFO_ADDED: {
        if (esd?.cart) {
          const cart = esd.cart;
          if (cart.currency) {
            eventParams.currency = cart.currency;
          }

          const value = Number(cart.total?.price || 0);
          if (value > 0) {
            eventParams.value = value;
          }

          if (cart.products?.length) {
            eventParams.items = cart.products.map(createGA4Item);
          }

          if (cart?.discounts?.[0]?.code) {
            eventParams.coupon = cart.discounts[0].code;
          }

          if (esd.pay_methods?.length) {
            eventParams.payment_type = esd.pay_methods[0];
          }
        }
        break;
      }

      case EMableEventName.PRODUCT_VIEWED: {
        if (esd?.act_product) {
          const product = esd.act_product;
          if (product.cur) {
            eventParams.currency = product.cur;
          }

          const value = Number(product.prc || 0);
          if (value > 0) {
            eventParams.value = value;
          }

          eventParams.items = [createGA4Item(product)];
        }
        break;
      }

      case EMableEventName.COLLECTION_VIEWED: {
        // if (esd?.collection && esd?.products) {
        //   const collection = esd.collection;
        //   eventParams.item_list_id = collection.id;
        //   eventParams.item_list_name = collection.name;
        //   eventParams.items = esd.products?.map(createGA4Item) || [];
        // }
        break;
      }

      case EMableEventName.LEAD: {
        if (esd?.cart?.currency) {
          eventParams.currency = esd.cart.currency;
        }
        eventParams.value = 1;
        break;
      }

      case EMableEventName.CUSTOM_EVENT: {
        if (esd?.cart) {
          const cart = esd.cart;
          if (!eventParams.currency && cart.currency) {
            eventParams.currency = cart.currency;
          }

          if (!eventParams.value && cart.total?.price) {
            const value = Number(cart.total.price);
            if (value > 0) {
              eventParams.value = value;
            }
          }

          if (!eventParams.items && cart.products?.length) {
            eventParams.items = cart.products.map(createGA4Item);
          }
        }
        break;
      }
    }
  } catch (error) {
    Logger.error('Error transforming Mable event to GA4 event', {
      error,
      esd,
      eventName,
    });
  }

  return {
    event_name: ga4EventName,
    event_params: eventParams,
  };
}
